import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useContext, useEffect, useState, useRef } from 'react'
import { Breadcrumbs, ListItemButton, Typography } from '@mui/material'

import { Document } from '../types'
import { countriesCurrencies } from '../lib'
import Chip from '../components/common/chip'
import { useSocket } from '../hooks/use-socket'
import NextIcon from '../assets/icons/next-icon.svg'
import { requestAccessToken } from '../api/auth.api'
import { AuthContext, UserContext } from '../contexts'
import { getBatchesBriefData } from '../api/app-data.api'
import { normalizeDocumentData, numberWithCommas } from '../utils'
import ContractDeleteIcon from '../assets/icons/contract-delete-icon.svg'
import { ReportSquareContainer } from '../components/reports-brief-management/report-square-container'

const CustomerCompanyBatchesManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const navigate = useNavigate()
  const { isAuth } = useContext(AuthContext)
  const { customerCompanyId } = useParams()
  const { userSession } = useContext(UserContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { t } = useTranslation(['customer-company-details-management', 'reports-management', 'documents-to-review-management', 'documents-completed-management'])

  const dataFullyLoadedRef = useRef<boolean>(false)
  const [numBatchesReady, setNumBatchesReady] = useState(0)
  const [numBatchesForReview, setNumBatchesForReview] = useState(0)
  const [numBatchesCompleted, setNumBatchesCompleted] = useState(0)
  const [dataFullyLoaded, setDataFullyLoaded] = useState<boolean>(false)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [documentsList, setDocumentsList] = useState<Array<Document>>([])

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on('addNewBatch', async (dataFromSocket: any) => {
      const currency = dataFromSocket.currency || 'cop'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (currency === selectedCurrency) {
        handleGetBatchesDataFromRemote()
      }
    })

    appDataSocket.on('updateBatch', async (dataFromSocket: any) => {
      const currency = dataFromSocket.currency || 'cop'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (currency === selectedCurrency) {
        handleGetBatchesDataFromRemote()
      }
    })

    appDataSocket.on('deleteBatch', async (dataFromSocket: any) => {
      const currency = dataFromSocket.currency || 'cop'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (currency === selectedCurrency) {
        handleGetBatchesDataFromRemote()
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    if (userSession) {
      if (userSession.role.indexOf('-client') === -1) {
        navigate('/404')
        return
      }
    }

    handleGetBatchesBriefData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  const handleGetBatchesBriefData = async () => {
    let localBatchesBriefDataToSet: any = {}
    const localBatchesBriefData = globalThis.sessionStorage.getItem(`batchesBriefData:${customerCompanyId}:${selectedCountry}`)

    if (localBatchesBriefData) {
      localBatchesBriefDataToSet = JSON.parse(localBatchesBriefData)
      handleSetInitialData(localBatchesBriefDataToSet)
    }

    handleGetBatchesDataFromRemote()
  }

  const handleGetBatchesDataFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getBatchesBriefData(selectedCountry, accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`batchesBriefData:${customerCompanyId}:${selectedCountry}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetBatchesBriefData > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    setNumBatchesReady(dataToSet.numBatchesReady)
    setNumBatchesForReview(dataToSet.numBatchesForReview)
    setNumBatchesCompleted(dataToSet.numBatchesCompleted)

    const documentsToReviewNormalizedData = []
    for (const element of dataToSet.batchesBriefData) {
      const documentData = { ...element }
      const documentNormalizedData = normalizeDocumentData(documentData) as Document
      // documentNormalizedData.uploadedByFullName = await handleGetUploadedByFullName(documentNormalizedData)
      documentNormalizedData.uploadedByFullName = ''
      documentsToReviewNormalizedData.push(documentNormalizedData)
    }

    documentsToReviewNormalizedData.sort((thisDocument: Partial<Document>, nextDocument: Partial<Document>) => {
      return Date.parse(nextDocument.createdAt) - Date.parse(thisDocument.createdAt)
    })

    setDocumentsList(documentsToReviewNormalizedData)

    dataFullyLoadedRef.current = true
    setDataFullyLoaded(true)
    setLoadingLocalData(false)
  }

  const handleTransformFileName = (fileName: string) => {
    const lastDotIndex = fileName.lastIndexOf('.')

    if (lastDotIndex === -1) {
      return fileName.toUpperCase()
    }

    const namePart = fileName.substring(0, lastDotIndex)
    const extension = fileName.substring(lastDotIndex)

    return namePart.toUpperCase() + extension
  }

  const handleGetDocumentChipStatus = (documentDataStatus: string) => {
    if (documentDataStatus === 'uploaded' || documentDataStatus === 'analyzing' || documentDataStatus === 'downloaded' || documentDataStatus === 'downloaded-n-analyzing') {
      return 'analyzing'
    }

    if (documentDataStatus.indexOf('error') !== -1) {
      return 'error'
    }

    if (documentDataStatus === 'stopped') {
      return 'disabled'
    }

    return documentDataStatus
  }

  const handleGetDocumentChipStatusText = (documentDataStatus: string) => {
    if (documentDataStatus === 'uploaded' || documentDataStatus === 'analyzing' || documentDataStatus === 'downloaded' || documentDataStatus === 'downloaded-n-analyzing') {
      return t('listItem.chipStatusTexts.analyzing', { ns: 'documents-to-review-management' })
    }

    if (documentDataStatus === 'error-downloading-file') {
      return t('listItem.chipStatusTexts.downloadError', { ns: 'documents-to-review-management' })
    }

    if (documentDataStatus === 'error-analyzing-file') {
      return t('listItem.chipStatusTexts.fileError', { ns: 'documents-to-review-management' })
    }

    if (documentDataStatus === 'stopped') {
      return t('listItem.chipStatusTexts.stoppedAnalysis', { ns: 'documents-to-review-management' })
    }

    if (documentDataStatus.indexOf('error') > -1) {
      return t('listItem.chipStatusTexts.cobreError', { ns: 'documents-to-review-management' })
    }

    if (documentDataStatus === 'analysis-completed') {
      return t('listItem.chipStatusTexts.completedAnalysis', { ns: 'documents-to-review-management' })
    }

    if (documentDataStatus === 'ready-for-disperse') {
      return t('listItem.chipStatusTexts.readyForDisperse', { ns: 'documents-completed-management' })
    }

    if (documentDataStatus === 'dispersing' || documentDataStatus === 'register-disperse-completed') {
      return t('listItem.chipStatusTexts.dispersing', { ns: 'documents-completed-management' })
    }

    if (documentDataStatus === 'completed') {
      return t('listItem.chipStatusTexts.completed', { ns: 'documents-completed-management' })
    }

    return t('listItem.chipStatusTexts.inProcess', { ns: 'documents-to-review-management' })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        sx={{ width: '100%' }}
        aria-label="breadcrumb"
      >
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate('/')}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.home')}
          </Typography>
        </ListItemButton>
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.batches')}
        </Typography>
      </Breadcrumbs>
      <div
        style={{ marginTop: '20px' }}
        className="reports-row-container"
      >
        <ReportSquareContainer
          showMainIcon
          loaderWidth={80}
          showWarningIcon
          showButtonGoDetails
          icon={ContractDeleteIcon}
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          dataFullyLoaded={dataFullyLoaded}
          mainData={`${numberWithCommas(numBatchesForReview)}`}
          handleButtonGoDetailsAction={() => navigate('/documents-to-review')}
          reportName={t('batches.names.forReview', { ns: 'reports-management' })}
          detailsButtonText={t('batches.buttonActionText', { ns: 'reports-management' })}
        />
        <ReportSquareContainer
          showMainIcon
          loaderWidth={80}
          showPendingIcon
          showButtonGoDetails
          icon={ContractDeleteIcon}
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          dataFullyLoaded={dataFullyLoaded}
          mainData={`${numberWithCommas(numBatchesReady)}`}
          handleButtonGoDetailsAction={() => navigate('/documents-ready')}
          reportName={t('batches.names.uploaded', { ns: 'reports-management' })}
          detailsButtonText={t('batches.buttonActionText', { ns: 'reports-management' })}
        />
        <ReportSquareContainer
          showMainIcon
          loaderWidth={80}
          showCheckIcon
          showButtonGoDetails
          icon={ContractDeleteIcon}
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          dataFullyLoaded={dataFullyLoaded}
          mainData={`${numberWithCommas(numBatchesCompleted)}`}
          handleButtonGoDetailsAction={() => navigate('/documents-completed')}
          reportName={t('batches.names.sent', { ns: 'reports-management' })}
          detailsButtonText={t('batches.buttonActionText', { ns: 'reports-management' })}
        />
      </div>
      <div className="customer-details-actions-bar-container">
        <div className="customer-details-actions-bar-title">{t('batches.actionsBar.title', { ns: 'reports-management' })}</div>
        {/* <TextField
        variant="standard"
        sx={textFieldWhiteBg}
        label={t('searchField.placeholder')}
        value={paymentToSearch}
        error={!!errors.paymentToSearch}
        helperText={errors.paymentToSearch}
        className="customer-details-actions-bar-search-input"
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            handleSetPaymentToSearch(paymentToSearch, true)
          }
        }}
        onChange={(event) => {
          handleSetPaymentToSearch(event.target.value, true)
        }}
        onBlur={() => {
          if (paymentToSearch === '') handleClearSearchInput()
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                aria-label="toggle password visibility"
                sx={{ color: '#107569', right: '10px', bottom: '3px' }}
              >
                <img
                  src={SearchIcon}
                  alt="search-icon"
                />
              </IconButton>
            </InputAdornment>
          )
        }}
      /> */}
      </div>
      <div className="table-header-container table-header-batches">
        <div className="text-header table-header-width-20">{t('batches.tableHeaders.date', { ns: 'reports-management' }).toUpperCase()}</div>
        <div className="text-header table-header-width-40">{t('batches.tableHeaders.name', { ns: 'reports-management' }).toUpperCase()}</div>
        <div className="text-header table-header-width-20 num-of-payments-batches">{t('batches.tableHeaders.numOfPayments', { ns: 'reports-management' }).toUpperCase()}</div>
        <div className="text-header table-header-width-20 status-batches">{t('batches.tableHeaders.status', { ns: 'reports-management' }).toUpperCase()}</div>
      </div>
      {loadingLocalData ? (
        <div className="documents-list-container">
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
        </div>
      ) : (
        <div className="documents-list-container">
          {documentsList.map((documentData: Document, index: number) => {
            let documentChipStatus = handleGetDocumentChipStatus(documentData.status)
            let documentChipStatusText = handleGetDocumentChipStatusText(documentData.status)

            return (
              <div
                key={index}
                onClick={() => {
                  if (
                    documentData.status !== 'stopped' &&
                    documentData.status !== 'uploaded' &&
                    documentData.status !== 'completed' &&
                    documentData.status !== 'analyzing' &&
                    documentData.status !== 'downloaded' &&
                    documentData.status !== 'dispersing' &&
                    documentData.status !== 'downloaded-n-analyzing' &&
                    documentData.status !== 'register-disperse-completed'
                  ) {
                    navigate(`/document-details/${documentData._id}`, { state: { previousPath: `customer-company-batches/${userSession?.belongsTo}` } })
                  }

                  if (documentData.status === 'dispersing' || documentData.status === 'register-disperse-completed' || documentData.status === 'completed') {
                    navigate(`/document-payments-details/${documentData._id}`, { state: { previousPath: `customer-company-batches/${userSession?.belongsTo}` } })
                  }
                }}
                className="documents-list-item-data-container"
              >
                <div className="batch-list-row-item row-item-width-20">{format(new Date(documentData.createdAt), 'dd/MM/yyyy | HH:mm:ss')}</div>
                <div className="batch-list-row-item row-item-width-40">{handleTransformFileName(documentData.originalName)}</div>
                <div className="batch-list-row-item row-item-width-20 num-of-payments-batches">
                  {documentData.status === 'analysis-completed' ? numberWithCommas(Number(documentData.totalOfRegistries)) : numberWithCommas(Number(documentData.totalOfNoveltiesReviewed))}
                </div>
                <div className="batch-list-row-item row-item-width-20 status-batches">
                  <Chip
                    status={documentChipStatus}
                    label={documentChipStatusText}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default CustomerCompanyBatchesManagement
